import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useCalculatorStore } from '@src/stores';
import SocketService from '@src/services/SocketService';
import { useRoomContext } from '@src/contexts';

import useCalculatorEngine from '../engine/useCalculatorEngine';
import { createBasicModeHandlers, createScientificModeHandlers, createConverterModeHandlers } from './modes';

import {
  TCalculatorMode,
  ICalculatorEngineActions,
  ICalculatorHandlers,
  ECalculatorSocketEvents,
  ICalculatorEngineState,
} from '@src/types';

type TProps = {
  isReadOnly?: boolean;
};

const useModeHandlers = (
  mode: TCalculatorMode,
  actions: ICalculatorEngineActions,
  state?: ICalculatorEngineState,
  meta?: { justCalculated: boolean }
): ICalculatorHandlers => {
  if (mode === 'scientific') return createScientificModeHandlers(actions, state, meta);

  if (mode === 'converter') return createConverterModeHandlers(actions);
  return createBasicModeHandlers(actions);
};

export const useCalculatorHandlers = ({ isReadOnly = false }: TProps = {}) => {
  const lastActionRef = useRef<{ action: string; actionButton?: string } | null>(null);

  const {
    isPersonalCalculatorVisible,
    sharedCalculatorData,
    sharedCalculatorState,
    selectedCalculatorMode,
    setSelectedCalculatorMode,
    setCalculatorState,
  } = useCalculatorStore(
    useShallow((state) => ({
      isPersonalCalculatorVisible: state.isPersonalCalculatorVisible,
      sharedCalculatorData: state.sharedCalculatorData,
      sharedCalculatorState: state.sharedCalculatorState,
      selectedCalculatorMode: state.selectedCalculatorMode,
      setSelectedCalculatorMode: state.setSelectedCalculatorMode,
      setCalculatorState: state.setCalculatorState,
    }))
  );

  const { currentPeer } = useRoomContext();

  // Use local state to track the current mode, initialized from the store
  const [localMode, setLocalMode] = useState<TCalculatorMode>(
    isReadOnly && sharedCalculatorData.settings?.mode ? sharedCalculatorData.settings.mode : selectedCalculatorMode
  );
  const [isTransitioning, setIsTransitioning] = useState(false);

  const { state, actions, meta } = useCalculatorEngine();

  const { display, operationText, error } = state;

  const modeHandlers = useModeHandlers(localMode, actions, state, meta);

  const {
    handleNumber: baseHandleNumber,
    handleOperator: baseHandleOperator,
    handleClear: baseHandleClear,
    handleEquals: baseHandleEquals,
    handleDecimal: baseHandleDecimal,
    handlePercent: baseHandlePercent,
    handleBackspace: baseHandleBackspace,
  } = modeHandlers;

  const shouldBroadcastOperations = useMemo(
    () => sharedCalculatorData.isSharedByMe && sharedCalculatorData.isOpen && !isReadOnly,
    [isReadOnly, sharedCalculatorData.isOpen, sharedCalculatorData.isSharedByMe]
  );

  const queueOperationBroadcast = useCallback(
    (action: string, actionButton?: string) => {
      if (!shouldBroadcastOperations) return;
      lastActionRef.current = { action, actionButton };
    },
    [shouldBroadcastOperations]
  );

  const handleNumber = useCallback(
    (value?: string) => {
      baseHandleNumber(value);
      queueOperationBroadcast('input', value);
    },
    [baseHandleNumber, queueOperationBroadcast]
  );

  const handleOperator = useCallback(
    (value?: string) => {
      baseHandleOperator(value);
      queueOperationBroadcast('operator', value);
    },
    [baseHandleOperator, queueOperationBroadcast]
  );

  const handleClear = useCallback(() => {
    baseHandleClear();
    queueOperationBroadcast('clear');
    setCalculatorState({
      expression: null,
      result: null,
      error: null,
    });
  }, [baseHandleClear, queueOperationBroadcast, setCalculatorState]);

  const handleEquals = useCallback(() => {
    baseHandleEquals();
    queueOperationBroadcast('equals');
  }, [baseHandleEquals, queueOperationBroadcast]);

  const handleDecimal = useCallback(() => {
    baseHandleDecimal();
    queueOperationBroadcast('decimal');
  }, [baseHandleDecimal, queueOperationBroadcast]);

  const handlePercent = useCallback(() => {
    baseHandlePercent();
    queueOperationBroadcast('percent');
  }, [baseHandlePercent, queueOperationBroadcast]);

  const handleBackspace = useCallback(() => {
    baseHandleBackspace();
    queueOperationBroadcast('backspace');
  }, [baseHandleBackspace, queueOperationBroadcast]);

  useEffect(() => {
    if (!shouldBroadcastOperations) {
      lastActionRef.current = null;
      return;
    }

    if (!lastActionRef.current) return;

    const { action, actionButton } = lastActionRef.current;
    // guarantee the emitted calculator sync payload always carries a non-empty expression as requested from BE team
    const expressionForSync = state.operationText ?? state.input ?? '';
    // Use the *display* as the synced "result" (main value shown to the user)
    const resultForSync = state.display ?? '';

    SocketService.emit(ECalculatorSocketEvents.CALCULATOR_OPERATIONS_SYNC, {
      calculatorOperation: {
        action,
        actionButton: actionButton ?? '',
        expression: expressionForSync,
        result: resultForSync,
        peer_info: {
          peer_id: currentPeer?.peer_info?.peer_id ?? '',
          peer_uuid: currentPeer?.peer_info?.peer_uuid ?? '',
          peer_name: currentPeer?.peer_name ?? '',
          peer_record_id: currentPeer?.record_id ?? '',
        },
      },
    });

    lastActionRef.current = null;
  }, [
    currentPeer?.peer_info?.peer_id,
    currentPeer?.peer_name,
    currentPeer?.peer_info?.peer_uuid,
    currentPeer?.record_id,
    shouldBroadcastOperations,
    state.display,
    state.input,
    state.operationText,
    state.result,
  ]);

  const lastSyncedSharedStateRef = useRef<{ expression: string; result: string }>({
    expression: '',
    result: '',
  });

  useEffect(() => {
    if (!isReadOnly || !sharedCalculatorData.isOpen) return;

    const { expression: sharedExpression, result: sharedResult } = sharedCalculatorState;
    const lastSynced = lastSyncedSharedStateRef.current;

    if (sharedExpression === lastSynced.expression && sharedResult === lastSynced.result) return;

    // Ignore the result string during hydration to prevent double formatting
    // The "result" in converter mode contains the formatted output "X Unit (Rate: Y)" which shouldn't be set as input
    const isConverterMode = sharedCalculatorData.settings?.mode === 'converter';
    const resultToHydrate = isConverterMode ? '' : sharedResult;

    let expressionToHydrate = sharedExpression;
    if (isConverterMode && sharedExpression) {
      // Split by space to remove unit, and remove commas
      expressionToHydrate = sharedExpression.split(' ')[0].replace(/,/g, '');
    }

    actions.hydrateSharedState(expressionToHydrate, resultToHydrate);
    lastSyncedSharedStateRef.current = {
      expression: sharedExpression,
      result: sharedResult,
    };
  }, [
    actions,
    isReadOnly,
    sharedCalculatorData.isOpen,
    sharedCalculatorData.settings?.mode,
    sharedCalculatorState,
    sharedCalculatorState.expression,
    sharedCalculatorState.result,
  ]);

  // Clear the calculator when switching to or from converter mode
  // Use separate states in future in case we want to the last input in each mode
  const handleModeChange = useCallback(
    (nextMode: TCalculatorMode) => {
      if (nextMode === localMode) return;

      setIsTransitioning(true);

      // Only clear the calculator if switching to or from converter mode
      if (nextMode === 'converter' || localMode === 'converter') {
        handleClear();
      }

      // Only update the store for personal calculator mode changes
      if (!isReadOnly) {
        setSelectedCalculatorMode(nextMode);
      }

      // Always update the local mode
      setLocalMode(nextMode);

      // If this is a shared calculator owned by the current user, emit the update
      if (sharedCalculatorData.isSharedByMe && !isReadOnly) {
        SocketService.emit(ECalculatorSocketEvents.UPDATE_CALCULATOR, {
          calculatorDetails: {
            mode: nextMode,
          },
        });
      }
    },
    [localMode, sharedCalculatorData.isSharedByMe, isReadOnly, handleClear, setSelectedCalculatorMode]
  );

  // Keyboard event handling
  useEffect(() => {
    if (!isPersonalCalculatorVisible || isReadOnly) return;

    const isEditableElement = (target: EventTarget | null): target is HTMLElement => {
      if (!(target instanceof HTMLElement)) return false;

      const tagName = target.tagName;
      return tagName === 'INPUT' || tagName === 'TEXTAREA' || target.isContentEditable;
    };

    const operatorKeyMap: Record<string, string> = {
      '+': '+',
      '-': '-',
      '*': '×',
      x: '×',
      X: '×',
      '/': '÷',
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isEditableElement(event.target) || isTransitioning) return;

      const { key } = event;

      if (/^[0-9]$/.test(key)) {
        event.preventDefault();
        handleNumber(key);
        return;
      }

      if (key === '.' || key === 'Decimal') {
        event.preventDefault();
        handleDecimal();
        return;
      }

      if (key === '%' || key === 'p' || key === 'P') {
        event.preventDefault();
        handlePercent();
        return;
      }

      if (key === 'Backspace') {
        event.preventDefault();
        handleBackspace();
        return;
      }

      if (key === 'Delete' || key === 'Escape' || key === 'c' || key === 'C') {
        event.preventDefault();
        handleClear();
        return;
      }

      if (key === 'Enter' || key === '=') {
        event.preventDefault();
        handleEquals();
        return;
      }

      const operator = operatorKeyMap[key];
      if (operator) {
        event.preventDefault();
        handleOperator(operator);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    isPersonalCalculatorVisible,
    handleClear,
    handleEquals,
    handleNumber,
    handlePercent,
    handleDecimal,
    handleOperator,
    handleBackspace,
    isReadOnly,
    isTransitioning,
  ]);

  // Sync mode with shared settings when shared calculator
  useEffect(() => {
    if (isReadOnly && sharedCalculatorData?.settings?.mode) {
      setLocalMode(sharedCalculatorData.settings.mode);
    }
  }, [isReadOnly, sharedCalculatorData?.settings?.mode]);

  // Sync localMode with store's selectedCalculatorMode for personal calculators
  // This ensures that when mode changes from outside (e.g., drawer header), Calculator component updates
  useEffect(() => {
    if (!isReadOnly && selectedCalculatorMode !== localMode) {
      setLocalMode(selectedCalculatorMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadOnly, selectedCalculatorMode]);

  return {
    mode: localMode,
    error,
    display,
    operationText,
    isTransitioning,
    sharedCalculatorData,
    isPersonalCalculatorVisible,
    handleClear,
    handleEquals,
    handleNumber,
    handlePercent,
    handleDecimal,
    handleOperator,
    handleBackspace,
    handleModeChange,
    setIsTransitioning,
  };
};

export default useCalculatorHandlers;
